import React, { useState, useMemo } from "react";
import { GenericTable } from "@shared/components/GenericTable";
import { TableColumn } from "@shared/types/table";
import { Users } from "lucide-react";
import { useForm } from "react-hook-form";
import { ConsumersFilter } from "./ConsumersFilter";
import { PageHeader } from "@shared/components/PageHeader";
import {
  useCurrency,
  useRemoteUtilityId,
} from "@shared/selectors/globalSelectors";
import { useParams } from "react-router-dom";
import { useBulkRequestConsumersList } from "@features/cx/disconnect/hooks";
import { PAGE_SIZE } from "@shared/utils/constants";

interface ConsumerData {
  id: string;
  accountNo: string;
  name: string;
  phone: string;
  category: string;
  subCategory: string;
  premise: string;
  outstanding: number;
  status: string;
}

interface ConsumerFilters {
  remote_utility_id?: number;
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  sub_category?: string;
  premise?: string;
}

const ConsumersBulkDisconnect = () => {
  const { id: batchId } = useParams<{ id: string }>();
  const remoteUtilityId = useRemoteUtilityId();
  const currency = useCurrency();
  const [page, setPage] = useState(1);

  const [filters, setFilters] = useState<ConsumerFilters>({
    page: 1,
    limit: PAGE_SIZE,
    remote_utility_id: Number(remoteUtilityId),
    search: "",
    category: "",
    sub_category: "",
    premise: "",
  });

  const { data: consumersListResponse, isLoading } =
    useBulkRequestConsumersList({
      batchId: batchId || "",
      remoteUtilityId: Number(remoteUtilityId),
      page: filters.page || 1,
      limit: filters.limit || PAGE_SIZE,
      searchData: filters.search,
      category: filters.category,
      subCategory: filters.sub_category,
      premise: filters.premise,
    });

  const consumersData = useMemo(() => {
    if (!consumersListResponse?.results) return [];

    return consumersListResponse.results.map((item: any) => ({
      id: item.id?.toString(),
      accountNo: item?.consumerNo || "N/A",
      name: (item?.firstName || "") + " " + (item?.lastName || ""),
      phone: item?.contactNo || "N/A",
      category: item.categoryDisplay || "N/A",
      subCategory: item.subCategoryDisplay || "N/A",
      premise: item?.territoryData?.service?.premise || "N/A",
      outstanding: item.outstandingBalance || 0,
      status: item.statusDisplay || "N/A",
    }));
  }, [consumersListResponse]);

  const form = useForm({
    defaultValues: {
      category: "",
      sub_category: "",
      premise: "",
    },
  });

  const columns: TableColumn<ConsumerData>[] = useMemo(
    () => [
      {
        key: "accountNo",
        header: "Account No",
      },
      { key: "name", header: "Name" },
      { key: "phone", header: "Phone" },
      { key: "category", header: "Category" },
      { key: "subCategory", header: "Sub Category" },
      { key: "premise", header: "Premise" },
      {
        key: "outstanding",
        header: "Outstanding",
        render: (val) => (
          <span>
            {currency} {val}
          </span>
        ),
      },
      {
        key: "status",
        header: "Status",
        type: "badge",
        badgeConfig: {
          getVariant: (val) => {
            if (val === "COMPLETED") return "success";
            if (val === "IN PROGRESS") return "secondary";
            if (val === "PENDING") return "outline";
            return "default";
          },
          getClassName: (val) => {
            if (val === "IN PROGRESS")
              return "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-50";
            if (val === "PENDING")
              return "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-50";
            return "";
          },
        },
      },
    ],
    [currency],
  );

  const handleApplyFilters = (newFilters: Partial<ConsumerFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: 1,
    }));
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters({
      page: 1,
      limit: PAGE_SIZE,
      remote_utility_id: Number(remoteUtilityId),
      search: "",
      category: "",
      sub_category: "",
      premise: "",
    });
    setPage(1);
    form.reset();
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    setFilters((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const handleSearch = (searchTerm: string) => {
    setFilters((prev) => ({
      ...prev,
      search: searchTerm,
      page: 1,
    }));
    setPage(1);
  };

  const totalItems = consumersListResponse?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  const pagination = useMemo(
    () => ({
      currentPage: page,
      totalPages,
      totalItems,
      itemsPerPage: PAGE_SIZE,
      startItem: (page - 1) * PAGE_SIZE + 1,
      endItem: Math.min(page * PAGE_SIZE, totalItems),
    }),
    [page, totalPages, totalItems],
  );

  return (
    <div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <Users className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <PageHeader title="Consumer Disconnection Requests" subtitle="" />
          </div>
        </div>
      </div>
      <div className="mt-6">
        <GenericTable
          data={consumersData}
          columns={columns}
          rowKey="id"
          onSearch={handleSearch}
          searchPlaceholder="Search consumers..."
          pagination={pagination}
          onPageChange={handlePageChange}
          filterPanel={
            <ConsumersFilter
              form={form}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
            />
          }
        />
      </div>
    </div>
  );
};

export default ConsumersBulkDisconnect;
